import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ChartContainer } from "../ui/chart";
import { useTradeJournal } from "../../context/TradeJournalContext";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format, parseISO } from "date-fns";
const PerformanceChart = () => {
  const { filteredEntries, timeFilter } = useTradeJournal();
  const chartData = React.useMemo(() => {
    const dataMap = new Map();
    const sortedEntries = [...filteredEntries].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    sortedEntries.forEach((entry) => {
      const date = entry.date;
      if (!dataMap.has(date)) {
        dataMap.set(date, {
          date,
          pnl: 0,
          trades: 0,
          cumulativePnl: 0,
          winRate: 0,
          wins: 0,
          losses: 0,
        });
      }
      const dayData = dataMap.get(date);
      dayData.pnl += entry.pnl;
      dayData.trades += 1;
      dayData.wins += entry.pnl > 0 ? 1 : 0;
      dayData.losses += entry.pnl <= 0 ? 1 : 0;
      dayData.winRate = (dayData.wins / dayData.trades) * 100;
    });
    const result = Array.from(dataMap.values());
    let runningTotal = 0;
    result.forEach((day) => {
      runningTotal += day.pnl;
      day.cumulativePnl = runningTotal;
    });
    return result;
  }, [filteredEntries]);
  const formatXAxis = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      if (timeFilter === "day") {
        return format(date, "HH:mm");
      } else if (timeFilter === "week") {
        return format(date, "EEE");
      } else if (timeFilter === "month") {
        return format(date, "dd MMM");
      } else {
        return format(date, "dd MMM");
      }
    } catch (error) {
      return dateStr;
    }
  };
  const formatTooltipDate = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      return format(date, "dd MMM yyyy");
    } catch (error) {
      return dateStr;
    }
  };
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border p-3 rounded-md shadow-md">
          <p className="font-medium mb-1">{formatTooltipDate(label)}</p>
          <div className="space-y-1">
            <p className="text-xs">
              <span className="inline-block w-3 h-3 bg-market-teal rounded-sm mr-2" />
              Daily P&L:{" "}
              <span
                className={
                  payload[0].value >= 0 ? "text-green-400" : "text-red-400"
                }
              >
                ₹{payload[0].value?.toFixed(2)}
              </span>
            </p>
            <p className="text-xs">
              <span className="inline-block w-3 h-3 bg-blue-400 rounded-sm mr-2" />
              Cumulative P&L:{" "}
              <span
                className={
                  payload[1].value >= 0 ? "text-green-400" : "text-red-400"
                }
              >
                ₹{payload[1].value?.toFixed(2)}
              </span>
            </p>
            {payload[2] && (
              <p className="text-xs">
                <span className="inline-block w-3 h-3 bg-yellow-400 rounded-sm mr-2" />
                Win Rate: {payload[2].value?.toFixed(0)}%
              </p>
            )}
            <p className="text-xs text-white/60">
              Trades: {payload[0].payload.trades}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };
  if (chartData.length === 0) {
    return;
  }
  const CustomizedDot = (props: any) => {
    const { cx, cy, value } = props;
    const color = value >= 0 ? "var(--color-cumulativePnl)" : "#ef4444";
    return <circle cx={cx} cy={cy} r={3} fill={color} stroke={color} />;
  };
  return (
    <Card className="intellectia-card">
      <CardHeader className="pb-0">
        <CardTitle className="text-lg">Performance Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ChartContainer
            config={{
              pnl: {
                color: "#10b981",
              },
              // Green for daily P&L
              cumulativePnl: {
                color: "#3b82f6",
              },
              // Blue for cumulative P&L
              winRate: {
                color: "#facc15",
              }, // Yellow for win rate
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
                barGap={0}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatXAxis}
                  stroke="#888"
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  stroke="#888"
                  tickFormatter={(value) => `₹${value}`}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#888"
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="pnl"
                  name="Daily P&L"
                  fill="var(--color-pnl)"
                  radius={[4, 4, 0, 0]}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.pnl >= 0 ? "var(--color-pnl)" : "#ef4444"}
                    />
                  ))}
                </Bar>
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="cumulativePnl"
                  name="Cumulative P&L"
                  stroke="var(--color-cumulativePnl)"
                  strokeWidth={2}
                  dot={<CustomizedDot />}
                  connectNulls
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="winRate"
                  name="Win Rate %"
                  stroke="var(--color-winRate)"
                  strokeWidth={2}
                  dot={{
                    r: 3,
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
};
export default PerformanceChart;
